import { useRef, useState, useEffect, useMemo  } from "react";
import {
  Paper,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import FileUpload from "../../components/FileUpload";
import ButtonMain from "../../components/Button";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import httpClient from "../../_util/api";
import { useDispatch } from 'react-redux';
import { hideLoader, showLoader } from "../../Store/mainSlice";
import { downloadCSV } from "../../components/Reports/downloadCSV";

const VendorApplications = () => {
  const dispatch = useDispatch();
  const snackbar_Ref = useRef();
  const fileInputRef = useRef();
  const [tabIndex, setTabIndex] = useState(0);
  const [vendorList, setVendorList] = useState([]);
  const [subCategory, setSubCategory] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [selectedFile, setSelectedFile] = useState({
    fileName: "",
    url: "",
  });
  const [csvData, setCsvData] = useState([]);

  useEffect(() => {
    const getVendorListHandler = async () => {
      try {
        const res = await httpClient.get(`/user/vendors`);
        if (res.status === 200) {
          setVendorList(res.data);
        }
      } catch (error) {
        console.error("Error fetching vendor list:", error);
      }
    };
    getVendorListHandler();
  }, []);

  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  const vendorName = useMemo(() => {
    if (selectedVendor) {
      return vendorList.find(e => e._id === selectedVendor)?.firstName || "";
    }
    return "";
  }, [selectedVendor, selectedVendor, vendorList]);

  const handleDownloadTemplate = async () => {
    if (!selectedVendor) {
      snackbar_Ref.current.showMessage("error", "Please select a Vendor");
      return;
    }

    dispatch(showLoader());
      const filter = {
        subCategory: "Approved Applicaiton By Vendor",
        vendorId: selectedVendor,
      };
    
    try {
      const res = await httpClient.post('/report/applicationSummary', filter);
      dispatch(hideLoader());
      if (res.status === 200) {
        downloadCSV(res.data.data, `${vendorName} Applications`)
        snackbar_Ref.current.showMessage("success", res.data.message, "", "i-check-circle");
      } else {
        snackbar_Ref.current.showMessage("error", "Failed to download report.", "", "i-error-circle");
      }
    } catch (error) {
      dispatch(hideLoader());
      snackbar_Ref.current.showMessage("error", error?.response?.data?.message || error.message || "Something went wrong.", "", "i-error-circle");
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
    const data = lines.slice(1).map((line) => {
      const values = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
      const obj = {};
      headers.forEach((header, i) => {
        obj[header] = values[i] || "";
      });
      return obj;
    });

    return data;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.type;

    if (fileType !== "text/csv") {
      snackbar_Ref.current.showMessage("error", "Please upload a CSV file.");
      return;
    }
  
    setSelectedFile({ fileName: file.name, url: "" });

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);
      const updatedData = parsed.map(e => {
        return {
          applicationNumber: e['Application Number'],
          amount: e['Approved Fee'],
        }
      })
      setCsvData(updatedData);
    };
    reader.onerror = () => {
      snackbar_Ref.current.showMessage("error", "Failed to read the file");
    };
    reader.readAsText(file);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleSave = async () => {
    if (!selectedFile.fileName) {
      snackbar_Ref.current.showMessage("error", "Please upload a file first");
      return;
    }

    if (!selectedVendor) {
      snackbar_Ref.current.showMessage("error", "Please select a Vendor");
      return;
    }

    const filter = {
      bulkApplications: csvData,
      vendorId: selectedVendor,
    };

    try {
      const res = await httpClient.post('/report/bulkUpdate', filter);
      dispatch(hideLoader());
      if (res.status === 200) {
        snackbar_Ref.current.showMessage("success", res.data.message, "", "i-check-circle");
      } else {
        snackbar_Ref.current.showMessage("error", "Failed to download report.", "", "i-error-circle");
      }
    } catch (error) {
      dispatch(hideLoader());
      snackbar_Ref.current.showMessage("error", error?.response?.data?.message || error.message || "Something went wrong.", "", "i-error-circle");
    }
  };
  
  return (
    <Paper
      sx={{
        height: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CustomizedSnackbars ref={snackbar_Ref} />
      <Box
        sx={{
          width: 500,
          border: "1px solid #ccc",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Partner Applications
        </Typography>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          centered
          sx={{ mb: 3 }}
        >
          <Tab label="Download Applications" />
          <Tab label="Update Applications" />
        </Tabs>

        {tabIndex === 0 && (
          <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Partner</InputLabel>
              <Select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                label="Select Vendor"
              >
                {vendorList?.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.firstName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ButtonMain
              variant="green"
              onClick={handleDownloadTemplate}
              sx={{ width: "100%" }}
            >
              Download
            </ButtonMain>
          </Box>
        )}

        {tabIndex === 1 && (
          <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Partner</InputLabel>
              <Select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                label="Sub Category"
              >
                {vendorList?.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.firstName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FileUpload
              type={"text/csv"}
              ref={fileInputRef}
              label="Upload CSV"
              file={selectedFile}
              onClick={handleUploadClick}
              onChange={handleFileChange}
              onRemove={() => {
                setSelectedFile({ fileName: "", url: "" });
                setCsvData([]);
              }}
            />
            <ButtonMain
              variant="green"
              onClick={handleSave}
              sx={{ width: "100%", mt: 2 }}
            >
              Upload
            </ButtonMain>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default VendorApplications;
